import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../api/axiosClient";

function decodeJWT(token: string): { sub?: string; role?: string } {
  try {
    const claims = JSON.parse(atob(token.split(".")[1]));
    // hp.user_role stores role names uppercase (ADMIN/BARBER/FRONT_DESK) — normalize
    // here, the single decode point, so every downstream role comparison ("admin",
    // "front_desk", etc.) doesn't have to know about that mismatch.
    return { ...claims, role: claims.role ? String(claims.role).toLowerCase() : claims.role };
  } catch {
    return {};
  }
}

export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await axiosClient.post("/register", data);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Registration failed");
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    data: { identifier: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await axiosClient.post("/login", data);
      const payload = res.data;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("accessToken", payload.access_token);
        window.localStorage.setItem("refreshToken", payload.refresh_token);
      }
      return payload;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Login failed");
    }
  }
);

interface AuthState {
  loading: boolean;
  error: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  identifier: string | null;
  role: string | null;
  userId: string | null;
  avatarUrl: string | null;
}

const ls = typeof window !== "undefined" ? window.localStorage : null;

const initialState: AuthState = {
  loading: false,
  error: null,
  accessToken: ls?.getItem("accessToken") ?? null,
  refreshToken: ls?.getItem("refreshToken") ?? null,
  identifier: ls?.getItem("identifier") ?? null,
  role: ls?.getItem("userRole") ?? null,
  userId: ls?.getItem("userId") ?? null,
  avatarUrl: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.identifier = null;
      state.role = null;
      state.userId = null;
      state.avatarUrl = null;
      ls?.removeItem("accessToken");
      ls?.removeItem("refreshToken");
      ls?.removeItem("identifier");
      ls?.removeItem("userRole");
      ls?.removeItem("userId");
    },
    setAvatarUrl(state, action: { payload: string | null }) {
      state.avatarUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.identifier = action.meta.arg.identifier;
        const decoded = decodeJWT(action.payload.access_token);
        state.role = decoded.role ?? null;
        state.userId = decoded.sub ?? null;
        ls?.setItem("userRole", decoded.role ?? "");
        ls?.setItem("userId", decoded.sub ?? "");
        ls?.setItem("identifier", action.meta.arg.identifier);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, setAvatarUrl } = authSlice.actions;
export default authSlice.reducer;